<template>
    <div>
        <SToolbar>
            <v-icon small class="mr-1">ic-20 ic-user ic-col-grey</v-icon>
            <v-toolbar-title>{{ $t('benutzer') }}</v-toolbar-title>
        </SToolbar>
        <v-skeleton-loader v-if="!record" type="article, actions" />
        <v-card v-else height="var(--usable-height)" class="main-grid">
            <LoadingOverlay :value="isSaving" absolute />

            <!-- Header with Buttons -->
            <div class="top-header">
                <light-toolbar dense>
                    <v-toolbar-title>
                        <span>{{ headerFirstPart }}</span>
                        <span v-if="headerSecondPart" class="record-title">{{ headerSecondPart }}</span>
                    </v-toolbar-title>
                </light-toolbar>
                <v-toolbar dense class="elevation-0" color="primary white--text">
                    <v-slide-group next-icon="ic-20 ic-next ic-col-white" prev-icon="ic-20 ic-prev ic-col-white">
                        <ActionBtn
                            v-if="showSaveBtn"
                            color="primary"
                            :disabled="!isValid"
                            :loading="isSaving"
                            @click.stop="save"
                        >
                            <span><KpIcon icon-cls="save" color="white" /> {{ $t('save') }}</span>
                        </ActionBtn>
                        <ActionBtn color="primary" @click.stop="cancel()">
                            <span> <KpIcon icon-cls="x" color="white" /> {{ $t('schliessen') }} </span>
                        </ActionBtn>
                        <ActionBtn
                            color="primary"
                            :disabled="disableGoToEventOrganizerButton"
                            @click.stop="goToEventOrganizer()"
                        >
                            <span>
                                <KpIcon icon-cls="eventorganizer-2" color="white" /> {{ $t('to_event_organizer') }}
                            </span>
                        </ActionBtn>
                        <ActionBtn
                            color="primary"
                            :disabled="disableCreateContactButton"
                            @click.stop="showConfirmCreateContactDialog()"
                        >
                            <span><KpIcon icon-cls="new" color="white" /> {{ $t('create_contact') }}</span>
                        </ActionBtn>
                    </v-slide-group>
                </v-toolbar>
            </div>

            <!-- Navigation -->
            <div class="navigation">
                <v-tabs vertical background-color="transparent" hide-slider>
                    <v-tab
                        v-for="navItem in navItems"
                        :key="navItem.id"
                        :disabled="!Number(record?.getId())"
                        class="nav-tab"
                        :to="{ name: navItem.routeName }"
                        exact
                    >
                        <KpIcon :icon-cls="`${navItem.icon}`" color="grey" active-color="black" left />
                        {{ navItem.titleFn ? navItem.titleFn() : navItem.title }}
                    </v-tab>
                </v-tabs>
            </div>

            <!-- Main content -->
            <div class="content">
                <router-view
                    ref="activeChild"
                    :record="record"
                    @input="isValid = $event"
                    @saving="isSaving = $event"
                    @save="save"
                />
            </div>
        </v-card>
        <ConfirmDialogVue
            v-model="showingConfirmCreateContactDialog"
            :title="$t('create_organizer_contact_title')"
            :message="$t('create_organizer_contact_message')"
            @confirm="createContact"
        />
    </div>
</template>

<script>
import { User, UserCollection } from '@/models/User';
import { getRoles } from '@/models/Role';
import { mapActions } from 'vuex';
import getRulesMixin from '@/mixins/get-form-rules';
import { errToStr, setBrowserTitle } from '@/helpers/tools';
import { hex_sha1 } from '@appvise/digest-sha1';
import ConfirmDialogVue from '@/components/ConfirmDialog.vue';
import { instance as api } from '@/helpers/api';

export default {
    name: 'UserEdit',
    components: {
        ConfirmDialogVue,
    },
    mixins: [getRulesMixin],

    async beforeRouteUpdate(to, from, next) {
        await this.save();
        if (to.params.id !== from.params.id) {
            this.record = null;
        }
        this.loadRecord(to.params.id);
        next();
    },

    data() {
        return {
            record: null,
            isValid: false,
            isSaving: false,
            roles: getRoles(),
            showingConfirmCreateContactDialog: false,
            navItems: [
                {
                    id: 'basisdaten',
                    routeName: 'user-edit',
                    icon: 'allincpast',
                    title: this.$t('basisdaten'),
                    showSaveBtn: true,
                },
                {
                    id: 'metadata',
                    routeName: 'user-edit-metadata',
                    icon: 'info',
                    title: this.$t('metadata'),
                    showSaveBtn: false,
                },
                {
                    id: 'relations',
                    routeName: 'user-edit-relations',
                    icon: 'link',
                    title: this.$t('relationen'),
                    showSaveBtn: false,
                },
                {
                    id: 'changelog',
                    routeName: 'user-edit-changelog',
                    icon: 'log',
                    title: this.$t('changelog'),
                    showSaveBtn: false,
                },
            ],
        };
    },

    computed: {
        showSaveBtn() {
            return this.activeNavItem?.showSaveBtn === true;
        },
        headerFirstPart() {
            return `${this.$t('benutzer_erfassen')}${this.headerSecondPart ? `: ` : ''}`;
        },
        headerSecondPart() {
            return this.record?.fullname;
        },
        browserTitle() {
            if (this.headerSecondPart) {
                return `${this.headerSecondPart} - ${this.$t('benutzer')} - ${this.activeNavItem?.title}`;
            } else {
                return this.headerFirstPart;
            }
        },
        activeNavItem() {
            const actRouteName = this.$route.name;
            const items = this.navItems.filter((navItem) => navItem.routeName === actRouteName);
            if (items?.length > 0) {
                return items[0];
            }
            return null;
        },
        disableGoToEventOrganizerButton() {
            return !this.record?.event_organizer?.address_id;
        },
        disableCreateContactButton() {
            return this.record.getId() <= 0 || !this.record?.event_organizer;
        },
    },

    watch: {
        browserTitle: {
            handler() {
                setBrowserTitle(this.getBrowserTitle());
            },
            immediate: true,
        },
    },

    async beforeDestroy() {
        if (!this.record?.isPhantom()) {
            await this.save();
        }
    },

    mounted() {
        this.loadRecord(this.$route.params.id);
    },

    methods: {
        ...mapActions(['runAsync']),

        async loadRecord(recordId) {
            // create a new record if requested, or load an existing one
            if (recordId === 'new' || !Number(recordId)) {
                this.record = new User({
                    active: true,
                });
                // premature return in case of new record
                return;
            }

            // load existing record
            this.record = null;
            try {
                const collection = new UserCollection();
                await this.runAsync(() => collection.query({ id: recordId }));
                const record = collection.first();
                if (record) {
                    this.record = record;
                }
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            }
        },

        async reload() {
            this.loadRecord(this.record?.getId());
        },

        cancel() {
            this.$router.push({ name: 'user' });
        },

        async save() {
            if (!this.record) {
                return false;
            }
            if (!this.isValid) {
                return false;
            }

            try {
                this.isSaving = true;

                // give the child a change to do things before we save
                // e.g. save block contents
                if (this.$refs.activeChild.beforeSave) {
                    if (!(await this.$refs.activeChild.beforeSave())) {
                        return false;
                    }
                }

                if (this.record?.newPw) {
                    this.record.password_change = hex_sha1(this.record?.newPw);
                }

                const isNew = this.record?.isPhantom();

                if (this.record?.hasUnsavedData()) {
                    await this.runAsync(async () => await this.record?.save());
                }

                if (isNew) {
                    // when a new rec is created, replace the route...
                    this.$router.replace({ name: 'user-edit', params: { id: this.record?.getId() } });
                }

                return true;
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.isSaving = false;
            }
        },

        getBrowserTitle() {
            return this.browserTitle;
        },

        goToEventOrganizer() {
            this.$router.push({ name: 'address-edit', params: { id: this.record?.event_organizer?.address_id } });
        },

        showConfirmCreateContactDialog() {
            this.showingConfirmCreateContactDialog = true;
        },

        async createContact() {
            const response = await api().post('/Address/addContactForUser', {
                address_id: this.record?.event_organizer?.address_id,
                user_id: this.record?.getId(),
                is_main_contact: true,
            });
            this.$router.push({ name: 'address-edit', params: { id: response?.data?.records?.[0]?.id } });
        },
    },
};
</script>

<style lang="scss" scoped>
.main-grid {
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 220px 1fr;
    grid-template-areas:
        'header header'
        'nav content'
        'nav bottom';
}

.top-header {
    grid-area: header;
}

.navigation {
    grid-area: nav;
    background-color: var(--toolbar-bg-color);
    padding: 12px;
    border-right: 1px solid var(--gray-border-color);
}

.content {
    grid-area: content;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.bottom-toolbar {
    grid-area: bottom;
    background-color: var(--toolbar-bg-color);
    padding: 10px 27px;
}

.nav-tab {
    height: initial !important;
    justify-content: start !important;
    padding: 5px 3px;
    margin: 3px;
    text-transform: none !important;
    font-weight: normal;
    font-size: 1rem;
    letter-spacing: 0;
    &.v-tab--active {
        color: var(--nearblack-color) !important;
    }
}

.record-title {
    color: initial;
}
</style>
